import { useState, useEffect } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

interface BudgetData {
  budget: {
    monthly_limit: number;
    current_spend: number;
    remaining: number;
  };
  activity: {
    crawls_this_month: number;
    current_month: string;
  };
  costs: {
    d1_reads: number;
    d1_writes: number;
    r2_storage_gb: number;
    r2_operations: number;
    vectorize_queries: number;
    workers_ai_requests: number;
  };
}

function Budget() {
  const [data, setData] = useState<BudgetData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchBudget();
  }, []);

  const fetchBudget = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/budget`);
      if (!response.ok) throw new Error('Failed to fetch budget');
      
      const budgetData = await response.json();
      setData(budgetData);
    } catch (err) {
      setError('Failed to load budget data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-state">Loading budget data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <div className="empty-state">
          <p>{error}</p>
          <button className="btn btn-primary mt-4" onClick={fetchBudget}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const spendPercentage = data
    ? (data.budget.current_spend / data.budget.monthly_limit) * 100
    : 0;

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1>Budget & Usage</h1>
        <p>Monitor Cloudflare resource usage and costs</p>
      </div>

      {/* Budget Overview */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-label">Monthly Budget</div>
          <div className="stat-card-value">${data?.budget.monthly_limit}</div>
          <div className="stat-card-detail">Target spend limit</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-label">Current Spend</div>
          <div className="stat-card-value">${data?.budget.current_spend.toFixed(2)}</div>
          <div className="stat-card-detail">
            {spendPercentage.toFixed(1)}% of budget
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-label">Remaining</div>
          <div className="stat-card-value">${data?.budget.remaining.toFixed(2)}</div>
          <div className="stat-card-detail">Available this month</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-label">Crawls This Month</div>
          <div className="stat-card-value">{data?.activity.crawls_this_month.toLocaleString()}</div>
          <div className="stat-card-detail">{data?.activity.current_month}</div>
        </div>
      </div>

      {/* Budget Progress Bar */}
      <div className="admin-section">
        <div className="admin-section-header">
          <h2>Budget Progress</h2>
        </div>
        <div className="card">
          <div style={{ 
            height: '24px', 
            backgroundColor: 'var(--color-border)', 
            borderRadius: '12px',
            overflow: 'hidden',
            marginBottom: '1rem'
          }}>
            <div style={{ 
              height: '100%', 
              width: `${Math.min(spendPercentage, 100)}%`,
              backgroundColor: spendPercentage > 80 ? 'var(--color-error)' : spendPercentage > 50 ? 'var(--color-warning)' : 'var(--color-success)',
              borderRadius: '12px',
              transition: 'width 0.3s ease'
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span className="text-secondary">$0</span>
            <span className={spendPercentage > 80 ? 'text-error' : spendPercentage > 50 ? 'text-warning' : 'text-success'}>
              ${data?.budget.current_spend.toFixed(2)} spent
            </span>
            <span className="text-secondary">${data?.budget.monthly_limit}</span>
          </div>
        </div>
      </div>

      {/* Cost Breakdown */}
      <div className="admin-section">
        <div className="admin-section-header">
          <h2>Estimated Cost Breakdown</h2>
        </div>
        <div className="card">
          <div className="breakdown-item">
            <span className="breakdown-label">D1 Database Reads</span>
            <span className="breakdown-value">{data?.costs.d1_reads.toLocaleString()} ops</span>
          </div>
          <div className="breakdown-item">
            <span className="breakdown-label">D1 Database Writes</span>
            <span className="breakdown-value">{data?.costs.d1_writes.toLocaleString()} ops</span>
          </div>
          <div className="breakdown-item">
            <span className="breakdown-label">R2 Storage</span>
            <span className="breakdown-value">{data?.costs.r2_storage_gb.toFixed(2)} GB</span>
          </div>
          <div className="breakdown-item">
            <span className="breakdown-label">R2 Operations</span>
            <span className="breakdown-value">{data?.costs.r2_operations.toLocaleString()} ops</span>
          </div>
          <div className="breakdown-item">
            <span className="breakdown-label">Vectorize Queries</span>
            <span className="breakdown-value">{data?.costs.vectorize_queries.toLocaleString()} queries</span>
          </div>
          <div className="breakdown-item">
            <span className="breakdown-label">Workers AI Requests</span>
            <span className="breakdown-value">{data?.costs.workers_ai_requests.toLocaleString()} requests</span>
          </div>
        </div>
      </div>

      {/* Pricing Reference */}
      <div className="admin-section">
        <div className="admin-section-header">
          <h2>Cloudflare Pricing Reference</h2>
        </div>
        <div className="card">
          <p className="text-secondary mb-4">
            Estimated costs based on Cloudflare's published pricing. Actual costs may vary.
          </p>
          <div className="breakdown-item">
            <span className="breakdown-label">D1 (Workers Paid)</span>
            <span className="breakdown-value">$0.75/million reads, $1.00/million writes</span>
          </div>
          <div className="breakdown-item">
            <span className="breakdown-label">R2</span>
            <span className="breakdown-value">$0.015/GB storage, $0.36/million Class A ops</span>
          </div>
          <div className="breakdown-item">
            <span className="breakdown-label">Vectorize</span>
            <span className="breakdown-value">$0.01/1000 vector queries</span>
          </div>
          <div className="breakdown-item">
            <span className="breakdown-label">Workers AI</span>
            <span className="breakdown-value">Varies by model (BGE embeddings ~$0.011/1000 tokens)</span>
          </div>
        </div>
      </div>

      {/* Note */}
      <div className="card" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', borderColor: 'var(--color-primary)' }}>
        <p style={{ color: 'var(--color-primary)' }}>
          <strong>Note:</strong> This dashboard currently shows estimated usage. 
          Full cost tracking integration with Cloudflare's billing API is planned for a future release.
        </p>
      </div>
    </div>
  );
}

export default Budget;
